import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
  Textarea,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { useController, useFieldArray, useWatch } from "react-hook-form";
import {
  HiOutlineCheck,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineX,
} from "react-icons/hi";

import { Button, IconButton } from "../../../components";
import { AiRegenerateIcon } from "../../../components/Icons/AiRegenerateIcon";
import { AiInterviewQuestionType } from "../../graphql";
import { useFeatureFlagForCurrentOrImpersonatedUser } from "../../graphql/hooks/useFeatureFlag";
import {
  QUESTION_SCORE_COLORS,
  QUESTION_SCORE_LABELS,
  QUESTION_SCORE_LEVELS,
} from "../AiRecording/Evaluation/scoring";
import { ComplianceWarning } from "./ComplianceWarning";
import AssistantFieldPreview from "./GuideAssistantV2/AssistantFieldPreview";
import {
  QuestionSuggestionPreviewState,
  suggestionFieldKey,
  SuggestionFieldTarget,
} from "./GuideAssistantV2/types";
import QuestionFormRubricRow, { ScoreBadge } from "./QuestionFormRubricRow";
import { QuestionWeightInput } from "./QuestionWeightInput";
import { useAiInterviewerFormContext } from "./useAiInterviewerFormContext";
import useCheckEeocCompliance from "./useCheckEeocCompliance";
import useCheckSubjectivity from "./useCheckSubjectivity";
import useGenerateQuestionEvaluation from "./useGenerateQuestionEvaluation";
import useGenerateQuestionFollowups from "./useGenerateQuestionFollowups";
import useRewriteQuestion from "./useRewriteQuestion";

const QuestionFormLabel: React.FC<
  { children: React.ReactNode } & React.ComponentProps<typeof FormLabel>
> = ({ children, ...props }) => (
  <FormLabel fontSize="sm" fontWeight="400" mb={1} color="gray.600" {...props}>
    {children}
  </FormLabel>
);

interface QuestionFormProps {
  index: number;
  readOnly?: boolean;
  canEnableQuestionWeights?: boolean;
  assistantPreview?:
    | (QuestionSuggestionPreviewState & { questionIndex: number })
    | null;
  assistantPreviewApplying?: boolean;
  assistantPreviewIgnoring?: boolean;
  onResolveAssistantPreviewField?: (
    target: SuggestionFieldTarget,
    decision: "accept" | "reject"
  ) => void;
  onResolveAssistantPreviewAll?: () => void;
  onIgnoreAssistantPreview?: () => void;
}

const QuestionForm: React.FC<QuestionFormProps> = ({
  index,
  readOnly,
  canEnableQuestionWeights,
  assistantPreview,
  assistantPreviewApplying,
  assistantPreviewIgnoring,
  onResolveAssistantPreviewField,
  onResolveAssistantPreviewAll,
  onIgnoreAssistantPreview,
}) => {
  const { control, register, watch, trigger, clearErrors } =
    useAiInterviewerFormContext();

  const autoImproveEnabled = useFeatureFlagForCurrentOrImpersonatedUser(
    "ai_interviewer:auto_improve_button"
  );

  const questionValue = useWatch({
    control,
    name: `questions.${index}.question`,
  });
  const qualificationCriteriaValue =
    useWatch({
      control,
      name: `questions.${index}.qualificationCriteria`,
    }) ?? "";
  const summaryCriteriaValue =
    useWatch({
      control,
      name: `questions.${index}.summaryCriteria`,
    }) ?? "";
  const { field: questionTypeField } = useController({
    control,
    name: `questions.${index}.type`,
    rules: {
      onChange: () => {
        const prevValue = questionType;
        if (prevValue === AiInterviewQuestionType.Score) {
          clearErrors(`questions.${index}.customWeight`);
          triggerCustomWeights();
        }
        checkSubjectivity();
      },
    },
  });
  const questionType = questionTypeField.value;

  const questions = watch("questions");
  const showCustomWeight =
    canEnableQuestionWeights &&
    questionType === AiInterviewQuestionType.Score &&
    watch("customQuestionWeightsEnabled");
  const triggerCustomWeights = (): void => {
    questions.forEach((q, i) => {
      if (q.type === AiInterviewQuestionType.Score) {
        trigger(`questions.${i}.customWeight`);
      }
    });
  };

  const { field: rubricField } = useController({
    control,
    name: `questions.${index}.rubric`,
  });
  const rubric = rubricField.value ?? [];

  const { generateQuestionEvaluation, loading } =
    useGenerateQuestionEvaluation(index);

  const { generateQuestionFollowups, loading: followupsLoading } =
    useGenerateQuestionFollowups(index);
  const { rewriteQuestion, loading: rewriteLoading } =
    useRewriteQuestion(index);
  const {
    checkEeocCompliance,
    eeocWarning,
    loading: eeocLoading,
  } = useCheckEeocCompliance(index);
  const {
    checkSubjectivity,
    subjectivityWarning,
    loading: subjectivityLoading,
  } = useCheckSubjectivity(index);
  const {
    isOpen: isSteeringModalOpen,
    onOpen: openSteeringModal,
    onClose: closeSteeringModal,
  } = useDisclosure();
  const [steeringInstructions, setSteeringInstructions] = useState("");

  const {
    fields: followupFields,
    append: appendFollowup,
    remove: removeFollowup,
  } = useFieldArray({
    control,
    name: `questions.${index}.followups` as any,
  });
  const followupValues =
    useWatch({
      control,
      name: `questions.${index}.followups`,
    }) ?? [];

  const hasAssistantPreview = !!assistantPreview;
  const previewFollowups = assistantPreview?.updatedQuestion.followups ?? [];
  const previewFollowupMaxIndex = hasAssistantPreview
    ? assistantPreview.affectedFields
        .filter(
          (target) =>
            target.field === "FOLLOWUP" && target.positionMode !== "APPEND"
        )
        .reduce((maxIndex, target) => {
          if (typeof target.index === "number") {
            return Math.max(maxIndex, target.index);
          }
          return maxIndex;
        }, -1)
    : -1;
  const followupRenderCount = hasAssistantPreview
    ? Math.max(
        followupValues.length,
        previewFollowups.length,
        previewFollowupMaxIndex + 1
      )
    : followupValues.length;

  const getPendingTarget = (
    field: SuggestionFieldTarget["field"],
    targetIndex?: number
  ): SuggestionFieldTarget | null => {
    if (!assistantPreview) {
      return null;
    }
    const matchingTarget = assistantPreview.affectedFields.find((target) => {
      const fieldTargetIndex = target.index ?? undefined;
      const expectedIndex = targetIndex ?? undefined;
      return target.field === field && fieldTargetIndex === expectedIndex;
    });
    if (!matchingTarget) {
      return null;
    }
    const key = suggestionFieldKey(matchingTarget);
    if (assistantPreview.resolvedFields[key]) {
      return null;
    }
    return matchingTarget;
  };

  const getPendingFollowupTarget = (
    targetIndex: number
  ): SuggestionFieldTarget | null => {
    if (!assistantPreview) {
      return null;
    }

    const absoluteTarget = getPendingTarget("FOLLOWUP", targetIndex);
    if (absoluteTarget) {
      return absoluteTarget;
    }

    const unresolvedAppendTargets = assistantPreview.affectedFields.filter(
      (target) =>
        target.field === "FOLLOWUP" &&
        target.positionMode === "APPEND" &&
        !assistantPreview.resolvedFields[suggestionFieldKey(target)]
    );
    const appendStartIndex = followupValues.length;
    const appendOffset = targetIndex - appendStartIndex;
    if (appendOffset < 0 || appendOffset >= unresolvedAppendTargets.length) {
      return null;
    }
    return unresolvedAppendTargets[appendOffset] ?? null;
  };

  const getPendingFollowupPreviewValue = (
    target: SuggestionFieldTarget,
    targetIndex: number
  ): string => {
    if (!assistantPreview) {
      return "";
    }
    const fieldChange = assistantPreview.fieldChanges.find(
      (change) => suggestionFieldKey(change) === suggestionFieldKey(target)
    );
    if (target.positionMode === "APPEND") {
      return fieldChange?.value ?? "";
    }
    return (
      assistantPreview.updatedQuestion.followups[targetIndex] ??
      previewFollowups[targetIndex] ??
      fieldChange?.value ??
      ""
    );
  };

  const unresolvedTargets = hasAssistantPreview
    ? assistantPreview.affectedFields.filter((target) => {
        const key = suggestionFieldKey(target);
        return !assistantPreview.resolvedFields[key];
      })
    : [];
  const canIgnoreEntireSuggestion =
    hasAssistantPreview && unresolvedTargets.length > 0;
  const assistantPreviewBusy =
    !!assistantPreviewApplying || !!assistantPreviewIgnoring;

  const renderAssistantFieldPreview = (
    target: SuggestionFieldTarget,
    originalValue: string,
    proposedValue: string
  ): React.ReactNode => {
    return (
      <AssistantFieldPreview
        target={target}
        originalValue={originalValue}
        proposedValue={proposedValue}
        isDisabled={assistantPreviewBusy}
        onResolve={onResolveAssistantPreviewField}
      />
    );
  };

  const renderAssistantPreviewActions = (): React.ReactNode => {
    if (!assistantPreview || unresolvedTargets.length === 0) {
      return null;
    }

    const changeCount = unresolvedTargets.length;

    return (
      <Flex
        w="100%"
        bg="gray.100"
        px={8}
        py={2.5}
        borderRadius="6px"
        gap={3}
        align="center"
      >
        <Button
          size="prismSm"
          colorScheme="green"
          variant="prismSecondary"
          minW="auto"
          leftIcon={<HiOutlineCheck size={16} />}
          onClick={() => onResolveAssistantPreviewAll?.()}
          isDisabled={readOnly || assistantPreviewBusy}
          isLoading={assistantPreviewApplying}
        >
          {`Accept all changes (${changeCount})`}
        </Button>
        {canIgnoreEntireSuggestion && (
          <Button
            size="prismSm"
            variant="prismSecondary"
            colorScheme="red"
            minW="auto"
            color="red.400"
            borderColor="red.400"
            leftIcon={<HiOutlineX size={16} />}
            onClick={() => onIgnoreAssistantPreview?.()}
            isDisabled={readOnly || assistantPreviewBusy}
            isLoading={assistantPreviewIgnoring}
          >
            {`Ignore all changes (${changeCount})`}
          </Button>
        )}
      </Flex>
    );
  };

  const disabled =
    readOnly ||
    loading ||
    followupsLoading ||
    rewriteLoading ||
    hasAssistantPreview;
  const emptyQuestion = !questionValue?.trim();

  const handleRewriteWithInstructions = async (): Promise<void> => {
    const instructions = steeringInstructions;
    setSteeringInstructions("");
    closeSteeringModal();
    await rewriteQuestion(instructions);
  };

  return (
    <Box position="relative" w="100%" minW={0}>
      <VStack align="stretch" spacing={4} w="100%" minW={0}>
        <Box>
          {eeocLoading && (
            <Flex align="center" gap={2} mb={1}>
              <Spinner size="xs" color="gray.400" />
              <Text fontSize="xs" color="gray.500">
                Checking compliance...
              </Text>
            </Flex>
          )}
          {assistantPreview && (
            <Box mb={2}>{renderAssistantPreviewActions()}</Box>
          )}
          {eeocWarning && <ComplianceWarning type="eeoc" mb={2} />}
          {(() => {
            const target = getPendingTarget("QUESTION");
            if (target && assistantPreview) {
              return renderAssistantFieldPreview(
                target,
                questionValue ?? "",
                assistantPreview.updatedQuestion.question
              );
            }
            return (
              <Textarea
                {...register(`questions.${index}.question`)}
                placeholder="Question text"
                resize="vertical"
                fontSize="sm"
                borderRadius="md"
                w="100%"
                rows={3}
                isDisabled={disabled}
                onBlur={checkEeocCompliance}
              />
            );
          })()}
        </Box>
        <Box>
          <VStack align="stretch" spacing={2} w="100%">
            {(() => {
              const renderFollowupRow = (fIdx: number) => (
                <Flex gap={2} align="flex-start" w="100%" minW={0}>
                  <Textarea
                    {...register(`questions.${index}.followups.${fIdx}`)}
                    placeholder={`Follow-up #${fIdx + 1}`}
                    resize="vertical"
                    fontSize="sm"
                    borderRadius="md"
                    borderColor="gray.200"
                    w="auto"
                    flex="1"
                    minW={0}
                    isDisabled={disabled}
                    minH={9}
                    rows={2}
                    onBlur={checkEeocCompliance}
                  />
                  {!readOnly && (
                    <>
                      <IconButton
                        size="sm"
                        variant="prismTertiary"
                        colorScheme="purple"
                        onClick={() => generateQuestionFollowups(fIdx)}
                        isDisabled={disabled || emptyQuestion}
                        aria-label="Regenerate follow-up question"
                        icon={<AiRegenerateIcon fontSize="lg" />}
                      />
                      <IconButton
                        size="sm"
                        variant="prismTertiary"
                        colorScheme="red"
                        onClick={() => removeFollowup(fIdx)}
                        isDisabled={disabled}
                        aria-label="Remove follow-up question"
                        icon={<HiOutlineTrash fontSize="lg" />}
                      />
                    </>
                  )}
                </Flex>
              );

              if (!hasAssistantPreview) {
                return followupFields.map((f, fIdx) => (
                  <Box key={f.id}>{renderFollowupRow(fIdx)}</Box>
                ));
              }

              // Preview mode: use Array.from to support additional rows beyond existing fields
              return Array.from({ length: followupRenderCount }, (_, fIdx) => {
                const isExistingFollowup = fIdx < followupValues.length;
                const rowTarget = getPendingFollowupTarget(fIdx);
                const previewValue = rowTarget
                  ? getPendingFollowupPreviewValue(rowTarget, fIdx)
                  : assistantPreview?.updatedQuestion.followups[fIdx] ??
                    previewFollowups[fIdx] ??
                    "";

                if (!isExistingFollowup && !rowTarget) {
                  return null;
                }

                return (
                  <Box key={followupFields[fIdx]?.id ?? `followup-${fIdx}`}>
                    {rowTarget && assistantPreview
                      ? renderAssistantFieldPreview(
                          rowTarget,
                          followupValues[fIdx] ?? "",
                          previewValue
                        )
                      : renderFollowupRow(fIdx)}
                  </Box>
                );
              });
            })()}
            {!readOnly && (
              <HStack
                align="center"
                w="100%"
                justify="flex-start"
                flexWrap="wrap"
                spacing={2}
              >
                <Button
                  size="xs"
                  onClick={() => generateQuestionFollowups()}
                  data-testid="generate-followup-bottom-btn"
                  variant="prismTertiary"
                  minWidth="120px"
                  colorScheme="purple"
                  leftIcon={<AiRegenerateIcon fontSize="md" />}
                  isDisabled={disabled || emptyQuestion}
                  isLoading={followupsLoading}
                >
                  Generate follow-up questions
                </Button>
                <Button
                  size="xs"
                  onClick={() => appendFollowup("")}
                  data-testid="add-question-bottom-btn"
                  variant="prismTertiary"
                  minWidth="120px"
                  leftIcon={<HiOutlinePlus fontSize="md" />}
                  isDisabled={disabled}
                  isLoading={followupsLoading}
                >
                  Add custom follow-up question
                </Button>
              </HStack>
            )}
          </VStack>
        </Box>
        <FormControl as="fieldset">
          <QuestionFormLabel>Evaluation method</QuestionFormLabel>
          <RadioGroup
            {...questionTypeField}
            colorScheme="blue"
            isDisabled={disabled}
            size="sm"
          >
            <Stack direction="row" spacing={6} flexWrap="wrap">
              <Radio value={AiInterviewQuestionType.Qualification}>
                <Text fontSize="sm">Qualification (yes/no)</Text>
              </Radio>
              <Radio value={AiInterviewQuestionType.Score}>
                <Text fontSize="sm">Numeric score (1-4)</Text>
              </Radio>
              <Radio value={AiInterviewQuestionType.Summary}>
                <Text fontSize="sm">Summarize only</Text>
              </Radio>
            </Stack>
          </RadioGroup>
        </FormControl>

        {showCustomWeight && (
          <QuestionWeightInput
            index={index}
            questions={questions}
            onBlur={triggerCustomWeights}
          />
        )}

        {/* Requirement Section */}
        {questionType === AiInterviewQuestionType.Qualification && (
          <FormControl mt={3}>
            <Flex align="center" gap={2} mb={1}>
              <QuestionFormLabel mb={0}>Evaluation criteria</QuestionFormLabel>
              {subjectivityLoading && (
                <Flex align="center" gap={2}>
                  <Spinner size="xs" color="gray.400" />
                  <Text fontSize="xs" color="gray.500">
                    Checking compliance...
                  </Text>
                </Flex>
              )}
            </Flex>
            {subjectivityWarning && (
              <ComplianceWarning type="subjectivity" mb={2} />
            )}
            {(() => {
              const target = getPendingTarget("QUALIFICATION_CRITERIA");
              if (target && assistantPreview) {
                return renderAssistantFieldPreview(
                  target,
                  qualificationCriteriaValue,
                  assistantPreview.updatedQuestion.qualificationCriteria ?? ""
                );
              }
              return (
                <Textarea
                  {...register(`questions.${index}.qualificationCriteria`)}
                  placeholder="Describe the qualification evaluation criteria..."
                  resize="vertical"
                  fontSize="sm"
                  borderRadius="md"
                  w="100%"
                  isDisabled={disabled}
                  onBlur={checkSubjectivity}
                />
              );
            })()}
          </FormControl>
        )}

        {/* Scoring Rubric Section */}
        {questionType === AiInterviewQuestionType.Score && (
          <FormControl mt={3}>
            <Flex align="center" gap={2} mb={1}>
              <QuestionFormLabel mb={0}>Scoring criteria</QuestionFormLabel>
              {subjectivityLoading && (
                <Flex align="center" gap={2}>
                  <Spinner size="xs" color="gray.400" />
                  <Text fontSize="xs" color="gray.500">
                    Checking compliance...
                  </Text>
                </Flex>
              )}
            </Flex>
            {subjectivityWarning && (
              <ComplianceWarning type="subjectivity" mb={2} />
            )}
            <VStack align="stretch" spacing={2} w="100%">
              {(() => {
                const target = getPendingTarget("RUBRIC");
                if (target && assistantPreview) {
                  return renderAssistantFieldPreview(
                    target,
                    rubric.join("\n"),
                    (assistantPreview.updatedQuestion.rubric ?? []).join("\n")
                  );
                }
                return QUESTION_SCORE_LEVELS.map((score, idx) => {
                  const rowTarget = getPendingTarget("RUBRIC", idx);
                  if (rowTarget && assistantPreview) {
                    return (
                      <Flex key={score} align="start" gap={2} w="100%">
                        <ScoreBadge
                          score={score}
                          label={QUESTION_SCORE_LABELS[score]}
                          color={QUESTION_SCORE_COLORS[score]}
                        />
                        <Box flex="1" minW={0}>
                          {renderAssistantFieldPreview(
                            rowTarget,
                            rubric[idx] ?? "",
                            assistantPreview.updatedQuestion.rubric[idx] ?? ""
                          )}
                        </Box>
                      </Flex>
                    );
                  }
                  return (
                    <Box key={score}>
                      <QuestionFormRubricRow
                        score={score}
                        label={QUESTION_SCORE_LABELS[score]}
                        color={QUESTION_SCORE_COLORS[score]}
                        value={rubric[idx] ?? ""}
                        onChange={(val) => {
                          rubric[idx] = val;
                          rubricField.onChange([...rubric]);
                        }}
                        onBlur={checkSubjectivity}
                        isDisabled={disabled}
                      />
                    </Box>
                  );
                });
              })()}
            </VStack>
          </FormControl>
        )}

        {/* Summary Criteria Section */}
        {questionType === AiInterviewQuestionType.Summary && (
          <FormControl mt={3}>
            <Flex align="center" gap={2} mb={1}>
              <QuestionFormLabel mb={0}>Summary criteria</QuestionFormLabel>
              {subjectivityLoading && (
                <Flex align="center" gap={2}>
                  <Spinner size="xs" color="gray.400" />
                  <Text fontSize="xs" color="gray.500">
                    Checking compliance...
                  </Text>
                </Flex>
              )}
            </Flex>
            {subjectivityWarning && (
              <ComplianceWarning type="subjectivity" mb={2} />
            )}
            {(() => {
              const target = getPendingTarget("SUMMARY_CRITERIA");
              if (target && assistantPreview) {
                return renderAssistantFieldPreview(
                  target,
                  summaryCriteriaValue,
                  assistantPreview.updatedQuestion.summaryCriteria ?? ""
                );
              }
              return (
                <Textarea
                  {...register(`questions.${index}.summaryCriteria`)}
                  placeholder="Describe the desired summary..."
                  resize="vertical"
                  fontSize="sm"
                  borderRadius="md"
                  w="100%"
                  isDisabled={disabled}
                  onBlur={checkSubjectivity}
                />
              );
            })()}
          </FormControl>
        )}

        {renderAssistantPreviewActions()}

        <Flex w="100%" justify="flex-end" mt={2} gap={2} flexWrap="wrap">
          {autoImproveEnabled && (
            <Button
              onClick={openSteeringModal}
              colorScheme="purple"
              size="sm"
              variant="prismSecondary"
              isLoading={rewriteLoading}
              isDisabled={disabled || emptyQuestion}
              leftIcon={<AiRegenerateIcon fontSize="lg" />}
            >
              Auto improve
            </Button>
          )}
          <Button
            onClick={generateQuestionEvaluation}
            colorScheme="blue"
            size="sm"
            variant="prismSecondary"
            isLoading={loading}
            isDisabled={disabled}
            backgroundColor="transparent"
          >
            Generate evaluation
          </Button>
        </Flex>

        <Modal
          isOpen={isSteeringModalOpen}
          onClose={() => {
            setSteeringInstructions("");
            closeSteeringModal();
          }}
          isCentered
          size="lg"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Add optional instructions</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text fontSize="sm" color="gray.600" mb={2}>
                Provide extra guidance for how the question should be improved.
              </Text>
              <Textarea
                value={steeringInstructions}
                onChange={(event) =>
                  setSteeringInstructions(event.target.value)
                }
                placeholder="e.g., emphasize hands-on experience, keep follow-ups concise"
                resize="vertical"
                fontSize="sm"
                borderRadius="md"
                rows={4}
              />
            </ModalBody>
            <ModalFooter gap={2}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSteeringInstructions("");
                  closeSteeringModal();
                }}
              >
                Cancel
              </Button>
              <Button
                colorScheme="purple"
                size="sm"
                variant="prismSecondary"
                onClick={handleRewriteWithInstructions}
                isDisabled={disabled}
              >
                Run auto improve
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </Box>
  );
};

export default QuestionForm;
